import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/Dashboard.css";

import 'bootstrap-icons/font/bootstrap-icons.css'; // Asegúrate de tener esta importación
import Navbar from '../components/Navbar';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [showCvOptionsModal, setShowCvOptionsModal] = useState(false); // State for CV options modal
  const [activeSection, setActiveSection] = useState('overview');
  const [mainContent, setMainContent] = useState('overview');
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        // Obtener datos del usuario
        const userResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/me`, {
          headers: { Authorization: token },
        });
        setUser(userResponse.data);

        // 🆕 Los admins pueden ver el dashboard normal también
        // Solo redirigir automáticamente si vienen de login (no si navegan manualmente)
        if (userResponse.data.role === 'admin' && window.location.pathname === '/dashboard') {
          // Permitir que los admins vean el dashboard normal
          // No redirigir automáticamente
        }

        // Obtener estado del usuario
        const statusResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/status`, {
          headers: { Authorization: token },
        });
        setStatus(statusResponse.data);
      } catch (error) {
        console.error("Error al obtener usuario:", error);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage("Selecciona un archivo PDF.");
      return;
    }

    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/upload-cv`, formData, {
        headers: { "Authorization": token, "Content-Type": "multipart/form-data" },
      });
      setMessage("CV subido con éxito.");
      setShowModal(false);
      window.location.reload();
    } catch (error) {
      setMessage("Error al subir el archivo.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const calculateProgress = () => {
    if (!status || !user) return 0;

    let total = 6; // Actividades fijas: CV subido, CV analizado, SoftSkills, HardSkills, AddictionDetection, Bullying
    let completed = 0;

    // Actividades fijas
    if (status.cvUploaded) completed++;
    if (status.cvAnalyzed) completed++;
    if (status.softSkillsSurvey) completed++;
    if (status.hardSkillsSurvey) completed++;
    if (status.AddictionDetectionSurvey) completed++;
    if (status.BullyingSurvey) completed++;

    // Encuestas vocacionales (solo se cuenta una, según el nivel académico)
    if (user.academic_level === "Secundaria") {
      total += 1;
      if (status.VocationalSecundarySurvey) completed++;
    } else if (user.academic_level === "Media Superior") {
      total += 1;
      if (status.VocationalUniversitySurvey) completed++;
    }

    return (completed / total) * 100;
  };


  const handleSectionChange = (section) => {
    setActiveSection(section);

    switch (section) {
      case 'cv':
        if (status?.cvUploaded) {
          navigate('/analyze-cv');
        } else {
          setShowModal(true);
        }
        break;

      case 'skills':
        // Mostrar menú de evaluaciones
        setMainContent('skills');
        break;

      default:
        setMainContent('overview');
        break;
    }
  };

  const renderSkillsContent = () => (
    <div className="skills-menu">
      <h3 className="mb-4">Evaluaciones Disponibles</h3>
      <div className="skills-grid">
        <div className="skill-card">
          <i className="bi bi-person-check icon-feature"></i>
          <h4>Habilidades Blandas</h4>
          <p>Evalúa tus habilidades interpersonales y sociales</p>
          {status?.softSkillsSurvey ? (
            <Link to="/soft-skills-results" className="btn btn-secondary">
              Ver Resultados
            </Link>
          ) : (
            <Link to="/soft-skills" className="btn btn-primary">
              Iniciar Evaluación
            </Link>
          )}
        </div>

        <div className="skill-card">
          <i className="bi bi-mortarboard icon-feature"></i>
          <h4>Inteligencias Múltiples</h4>
          <p>Descubre tus tipos de inteligencia predominantes</p>
          {status?.hardSkillsSurvey ? (
            <Link to="/hard-skills-results" className="btn btn-secondary">
              Ver Resultados
            </Link>
          ) : (
            <Link to="/hard-skills" className="btn btn-primary">
              Iniciar Evaluación
            </Link>
          )}
        </div>

        <div className="skill-card">
          <i className="bi bi-prescription2 icon-feature"></i>
          <h4>Detección de Adicciones</h4>
          <p>Analiza si tienes problemas con adicciones</p>
          {status?.AddictionDetectionSurvey ? (
            <Link to="/addiction-detection-results" className="btn btn-secondary">
              Ver Resultados
            </Link>
          ) : (
            <Link to="/addiction-detection" className="btn btn-primary">
              Iniciar Evaluación
            </Link>
          )}
        </div>

        <div className="skill-card">
          <i className="bi bi-person-exclamation icon-feature"></i>
          <h4>Acoso, Bullying y Violencia de Género</h4>
          <p>Analiza si sufres problemas de violencia</p>
          {status?.BullyingSurvey ? (
            <Link to="/Bullying-results" className="btn btn-secondary">
              Ver Resultados
            </Link>
          ) : (
            <Link to="/Bullying" className="btn btn-primary">
              Iniciar Evaluación
            </Link>
          )}
        </div>

        <div className="skill-card">
          <i className="bi bi-person-lines-fill icon-feature"></i>
          <h4>Datos Demográficos</h4>
          <p>Información personal y académica para tu perfil</p>
          {status?.demographicSurveyCompleted ? (
            <Link to="/demographic-results" className="btn btn-secondary">
              Ver Datos
            </Link>
          ) : (
            <Link to="/demographic-survey" className="btn btn-primary">
              Completar Cuestionario
            </Link>
          )}
        </div>

        {/* Encuesta vocacional según nivel académico */}
        {user?.academic_level === "Secundaria" && (
          <div className="skill-card">
            <i className="bi bi-lightbulb-fill icon-feature"></i>
            <h4>Orientación Vocacional para Nivel Medio Superior</h4>
            <p>Responde este test para saber sobre tu Orientación Vocacional Nivel Medio Superior</p>
            {status?.VocationalSecundarySurvey ? (
              <Link to="/vocational-results" className="btn btn-secondary">
                Ver Resultados
              </Link>
            ) : (
              <Link to="/vocational" className="btn btn-primary">
                Iniciar Evaluación
              </Link>
            )}
          </div>
        )}

        {user?.academic_level === "Media Superior" && (
          <div className="skill-card">
            <i className="bi bi-lightbulb-fill icon-feature"></i>
            <h4>Orientación Vocacional para Nivel Superior</h4>
            <p>Responde este test para saber sobre tu Orientación Vocacional Nivel Superior</p>
            {status?.VocationalUniversitySurvey ? (
              <Link to="/vocationalUniversity-results" className="btn btn-secondary">
                Ver Resultados
              </Link>
            ) : (
              <Link to="/vocationalUniversity" className="btn btn-primary">
                Iniciar Evaluación
              </Link>
            )}
          </div>
        )}



      </div>
    </div>
  );


  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const closeSidebar = () => {
    setShowSidebar(false);
  };

  return (
    <>
      <Navbar
        showSidebar={showSidebar}
        toggleSidebar={toggleSidebar}
        handleLogout={handleLogout}
      />

      {/* Dashboard Layout */}
      <div className="dashboard-layout">
        {/* Overlay para cerrar el sidebar en móvil */}
        <div
          className={`sidebar-overlay ${showSidebar ? 'show' : ''}`}
          onClick={closeSidebar}
        ></div>

        {/* Sidebar */}
        <div className={`dashboard-sidebar ${showSidebar ? 'show' : ''}`}>
          <div className="sidebar-header">
            <h3>Panel de Control</h3>
          </div>

          <div className="sidebar-menu">
            <button
              className={`menu-item ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => handleSectionChange('overview')}
            >
              <i className="bi bi-grid-1x2-fill"></i>
              Vista General
            </button>
            <button
              className={`menu-item ${activeSection === 'skills' ? 'active' : ''}`}
              onClick={() => handleSectionChange('skills')}
            >
              <i className="bi bi-lightbulb-fill"></i>
              Evaluación de Habilidades
            </button>
          </div>

          {/* Botón de admin si el usuario tiene ese rol */}
          {user?.role === 'admin' && (
            <Link to="/admin-dashboard" className="sidebar-admin">
              <i className="bi bi-shield-check"></i>
              Panel de Administración
            </Link>
          )}

          {/* Botón de cerrar sesión */}
          <button
            className="sidebar-logout"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-left"></i>
            Cerrar Sesión
          </button>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          {mainContent === 'skills' ? (
            renderSkillsContent()
          ) : (
            <>
              <div className="dashboard-header">
                <div className="header-welcome">
                  <h2>Bienvenido de nuevo, {user?.name}</h2>
                  <p>Aquí está el resumen de tu progreso</p>
                </div>
                <div className="progress-circle-container">
                  <div
                    className="progress-circle"
                    style={{ "--progress": `${calculateProgress() * 3.6}deg` }}
                  >
                    <div className="progress-circle-content">
                      <div className="progress-value">{Math.round(calculateProgress())}%</div>
                      <div className="progress-text">Completado</div>
                    </div>
                  </div>
                </div>
              </div>


              <div className="general-summary-card">
                <div className="general-summary-content">
                  <i className="bi bi-graph-up-arrow summary-icon"></i>
                  <div className="summary-text">
                    <h5 className="summary-title">Resumen General</h5>
                    <p className="summary-description">
                      Consulta un resumen visual de todas tus evaluaciones realizadas.
                    </p>
                  </div>
                </div>
                <Link to="/general-overview" className="btn btn-light fw-bold summary-button">
                  Ver Resumen
                </Link>
              </div>

              {/* Botón de Panel de Administración - Solo para admins */}
              {user?.role === 'admin' && (
                <div className="admin-panel-card">
                  <div className="admin-panel-content">
                    <i className="bi bi-shield-check admin-panel-icon"></i>
                    <div className="admin-panel-text">
                      <h5 className="admin-panel-title">Panel de Administración</h5>
                      <p className="admin-panel-description">
                        Gestiona usuarios, estadísticas y configuraciones del sistema.
                      </p>
                    </div>
                  </div>
                  <Link to="/admin-dashboard" className="btn btn-danger fw-bold admin-panel-button">
                    <i className="bi bi-shield-check me-2"></i>
                    Ir al Panel
                  </Link>
                </div>
              )}


              <div className="dashboard-content">
                <div className="dashboard-stats">
                  {/* Datos Demográficos - PRIMERO */}
                  <div className={`stat-card ${status?.demographicSurveyCompleted ? 'completed' : 'pending'}`}>
                    <i className="bi bi-person-lines-fill icon-feature"></i>
                    <div className="stat-info">
                      <h4>Datos Demográficos</h4>
                      <p>
                        <span className={`status-dot ${status?.demographicSurveyCompleted ? 'completed' : 'pending'}`}></span>
                        {status?.demographicSurveyCompleted ? 'Completado' : 'Pendiente'}
                      </p>
                      {status?.demographicSurveyCompleted ? (
                        <Link to="/demographic-results" className="btn btn-secondary mt-2">
                          <i className="bi bi-file-earmark-text"></i> Ver Datos
                        </Link>
                      ) : (
                        <Link to="/demographic-survey" className="btn btn-primary mt-2">
                          <i className="bi bi-pencil"></i> Completar Cuestionario
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className={`stat-card cv-card ${status?.cvUploaded ? 'completed' : 'pending'}`}>
                    <i className="bi bi-file-earmark-check icon-feature"></i>
                    <div className="stat-info">
                      <h4>Estado del CV</h4>
                      <p>
                        <span className={`status-dot ${status?.cvUploaded ? 'completed' : 'pending'}`}></span>
                        {status?.cvUploaded ? 'Subido' : 'Pendiente'}
                      </p>
                      {!status?.cvUploaded && (
                        <div className="button-container">
                          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                            <i className="bi bi-upload"></i> Subir CV
                          </button>
                        </div>
                      )}
                      {status?.cvUploaded && (
                        <div className="button-container">
                          <button
                            className="btn btn-primary"
                            onClick={() => setShowCvOptionsModal(true)}
                            style={{ width: '100%' }}
                          >
                            <i className="bi bi-gear-fill me-2"></i> Opciones
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={`stat-card ${status?.cvAnalyzed ? 'completed' : 'pending'}`}>
                    <i className="bi bi-chat-dots icon-feature"></i>
                    <div className="stat-info">
                      <h4>Entrevista</h4>
                      <p>
                        <span className={`status-dot ${status?.cvAnalyzed ? 'completed' : 'pending'}`}></span>
                        {status?.cvAnalyzed ? 'Completada' : 'Pendiente'}
                      </p>
                      {status?.cvAnalyzed ? (
                        <Link to="/interview-results" className="btn btn-secondary mt-2">
                          <i className="bi bi-graph-up"></i> Ver Resultados
                        </Link>
                      ) : (
                        <span className="text-muted mt-2 d-block">Sube y analiza tu CV primero</span>
                      )}
                    </div>
                  </div>

                  <div className={`stat-card ${status?.softSkillsSurvey ? 'completed' : 'pending'}`}>
                    <i className="bi bi-person-check icon-feature"></i>
                    <div className="stat-info">
                      <h4>Habilidades Blandas</h4>
                      <p>
                        <span className={`status-dot ${status?.softSkillsSurvey ? 'completed' : 'pending'}`}></span>
                        {status?.softSkillsSurvey ? 'Completado' : 'Pendiente'}
                      </p>
                      {status?.softSkillsSurvey ? (
                        <Link to="/soft-skills-results" className="btn btn-secondary mt-2">
                          <i className="bi bi-graph-up"></i> Ver Resultados
                        </Link>
                      ) : (
                        <Link to="/soft-skills" className="btn btn-primary mt-2">
                          <i className="bi bi-pencil"></i> Realizar Evaluación
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className={`stat-card ${status?.hardSkillsSurvey ? 'completed' : 'pending'}`}>
                    <i className="bi bi-mortarboard icon-feature"></i>
                    <div className="stat-info">
                      <h4>Inteligencias Múltiples</h4>
                      <p>
                        <span className={`status-dot ${status?.hardSkillsSurvey ? 'completed' : 'pending'}`}></span>
                        {status?.hardSkillsSurvey ? 'Completado' : 'Pendiente'}
                      </p>
                      {status?.hardSkillsSurvey ? (
                        <Link to="/hard-skills-results" className="btn btn-secondary mt-2">
                          <i className="bi bi-graph-up"></i> Ver Resultados
                        </Link>
                      ) : (
                        <Link to="/hard-skills" className="btn btn-primary mt-2">
                          <i className="bi bi-pencil"></i> Realizar Evaluación
                        </Link>
                      )}
                    </div>
                  </div>
                  {/* Addiction_Detection */}
                  <div className={`stat-card ${status?.AddictionDetectionSurvey ? 'completed' : 'pending'}`}>
                    <i className="bi bi-prescription2 icon-feature"></i>
                    <div className="stat-info">
                      <h4>Detección de Adicciones</h4>
                      <p>
                        <span className={`status-dot ${status?.AddictionDetectionSurvey ? 'completed' : 'pending'}`}></span>
                        {status?.AddictionDetectionSurvey ? 'Completado' : 'Pendiente'}
                      </p>
                      {status?.AddictionDetectionSurvey ? (
                        <Link to="/addiction-detection-results" className="btn btn-secondary mt-2">
                          <i className="bi bi-graph-up"></i> Ver Resultados
                        </Link>
                      ) : (
                        <Link to="/addiction-detection" className="btn btn-primary mt-2">
                          <i className="bi bi-pencil"></i> Realizar Evaluación
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Encuesta Vocacional para Secundaria */}
                  {user?.academic_level === "Secundaria" && (
                    <div className={`stat-card ${status?.VocationalSecundarySurvey ? 'completed' : 'pending'}`}>
                      <i className="bi bi-lightbulb icon-feature"></i>
                      <div className="stat-info">
                        <h4>Orientación Vocacional para Nivel Medio Superior</h4>
                        <p>
                          <span className={`status-dot ${status?.VocationalSecundarySurvey ? 'completed' : 'pending'}`}></span>
                          {status?.VocationalSecundarySurvey ? 'Completado' : 'Pendiente'}
                        </p>
                        {status?.VocationalSecundarySurvey ? (
                          <Link to="/vocational-results" className="btn btn-secondary mt-2">
                            <i className="bi bi-graph-up"></i> Ver Resultados
                          </Link>
                        ) : (
                          <Link to="/vocational" className="btn btn-primary mt-2">
                            <i className="bi bi-pencil"></i> Realizar Evaluación
                          </Link>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Encuesta Vocacional para Media Superior */}
                  {user?.academic_level === "Media Superior" && (
                    <div className={`stat-card ${status?.VocationalUniversitySurvey ? 'completed' : 'pending'}`}>
                      <i className="bi bi-book icon-feature"></i>
                      <div className="stat-info">
                        <h4>Orientación Vocacional para Nivel Superior</h4>
                        <p>
                          <span className={`status-dot ${status?.VocationalUniversitySurvey ? 'completed' : 'pending'}`}></span>
                          {status?.VocationalUniversitySurvey ? 'Completado' : 'Pendiente'}
                        </p>
                        {status?.VocationalUniversitySurvey ? (
                          <Link to="/vocationalUniversity-results" className="btn btn-secondary mt-2">
                            <i className="bi bi-graph-up"></i> Ver Resultados
                          </Link>
                        ) : (
                          <Link to="/vocationalUniversity" className="btn btn-primary mt-2">
                            <i className="bi bi-pencil"></i> Realizar Evaluación
                          </Link>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Encuesta de violencia de género - (única instancia) */}
                  <div className={`stat-card ${status?.BullyingSurvey ? 'completed' : 'pending'}`}>
                    <i className="bi bi-person-exclamation icon-feature"></i>
                    <div className="stat-info">
                      <h4>Acoso, Violencia de género y Bullying</h4>
                      <p>
                        <span className={`status-dot ${status?.BullyingSurvey ? 'completed' : 'pending'}`}></span>
                        {status?.BullyingSurvey ? 'Completado' : 'Pendiente'}
                      </p>
                      {status?.BullyingSurvey ? (
                        <Link to="/Bullying-results" className="btn btn-secondary mt-2">
                          <i className="bi bi-graph-up"></i> Ver Resultados
                        </Link>
                      ) : (
                        <Link to="/Bullying" className="btn btn-primary mt-2">
                          <i className="bi bi-pencil"></i> Realizar Evaluación
                        </Link>
                      )}
                    </div>
                  </div>




                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h4>Upload CV</h4>
              <button className="close-button" onClick={() => setShowModal(false)}>
                ✖️
              </button>
            </div>
            <div className="modal-body">
              <div className="file-upload-container">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setFile(e.target.files[0])}
                  id="cv-upload"
                />
                <label htmlFor="cv-upload">
                  📤 Choose PDF File
                </label>
              </div>
              {file && <div className="selected-file">{file.name}</div>}
              {message && <p className="message">{message}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpload}>
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CV Options Modal */}
      {showCvOptionsModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h4>Opciones de CV</h4>
              <button className="close-button" onClick={() => setShowCvOptionsModal(false)}>
                ✖️
              </button>
            </div>
            <div className="modal-body">
              <div className="d-flex flex-column gap-3">
                <Link to="/view-cv" className="btn btn-primary w-100">
                  <i className="bi bi-eye me-2"></i> Ver CV
                </Link>
                {!status?.cvAnalyzed && (
                  <Link to="/analyze-cv" className="btn btn-secondary w-100">
                    <i className="bi bi-search me-2"></i> Analizar CV
                  </Link>
                )}
                {(status?.softSkillsSurvey || status?.hardSkillsSurvey || status?.AddictionDetectionSurvey || status?.VocationalSecundarySurvey || status?.VocationalUniversitySurvey || status?.BullyingSurvey) && (
                  <Link to="/generated-cv" className="btn btn-success w-100">
                    <i className="bi bi-file-earmark-plus me-2"></i> Generar CV
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;